import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc, updateDoc, onSnapshot, serverTimestamp } from "firebase/firestore";
import { Helmet } from "@dr.pogodin/react-helmet";
import { db } from "../firebaseConfig";
import { buildSlides, Slide } from "../slideshow";
import "../css/slideshow.css";
import "../css/remote.css";

export default function RemoteControlPage() {
    const { sessionId } = useParams();
    const [status, setStatus] = useState("loading"); // loading | ready | error
    const [talk, setTalk] = useState(null);
    const [current, setCurrent] = useState(0);
    const [isPortrait, setIsPortrait] = useState(
        typeof window !== "undefined" ? window.innerHeight > window.innerWidth : false
    );

    // Resolve the session code to a talk, then load that talk once.
    useEffect(() => {
        if (!sessionId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const sessionSnap = await getDoc(doc(db, "remoteSessions", sessionId));
                if (cancelled) return;
                if (!sessionSnap.exists()) {
                    setStatus("error");
                    return;
                }
                const sessionData = sessionSnap.data();
                if (typeof sessionData.current === "number") setCurrent(sessionData.current);

                const talkSnap = await getDoc(doc(db, "talks", sessionData.talkId));
                if (cancelled) return;
                if (!talkSnap.exists()) {
                    setStatus("error");
                    return;
                }
                setTalk(talkSnap.data());
                setStatus("ready");
            } catch (e) {
                console.error("Failed to load remote session", e);
                if (!cancelled) setStatus("error");
            }
        })();
        return () => { cancelled = true; };
    }, [sessionId]);

    // Live-sync the slide position — updates from the presenter (or another
    // remote) land here automatically.
    useEffect(() => {
        if (!sessionId || status !== "ready") return undefined;
        const unsub = onSnapshot(doc(db, "remoteSessions", sessionId), (snap) => {
            if (!snap.exists()) return;
            const data = snap.data();
            if (typeof data.current === "number") setCurrent(data.current);
        });
        return () => unsub();
    }, [sessionId, status]);

    useEffect(() => {
        const onResize = () => setIsPortrait(window.innerHeight > window.innerWidth);
        window.addEventListener("resize", onResize);
        window.addEventListener("orientationchange", onResize);
        return () => {
            window.removeEventListener("resize", onResize);
            window.removeEventListener("orientationchange", onResize);
        };
    }, []);

    const slides = talk ? buildSlides(talk) : [];
    const total = slides.length;
    const safeCurrent = total > 0 ? Math.min(Math.max(current, 0), total - 1) : 0;

    const goTo = useCallback((idx) => {
        if (!sessionId || total === 0) return;
        const wrapped = ((idx % total) + total) % total;
        setCurrent(wrapped); // optimistic — Firestore will confirm
        updateDoc(doc(db, "remoteSessions", sessionId), {
            current: wrapped,
            updatedAt: serverTimestamp(),
        }).catch((e) => console.error("Failed to send remote command", e));
    }, [sessionId, total]);

    const prev = useCallback(() => goTo(safeCurrent - 1), [goTo, safeCurrent]);
    const next = useCallback(() => goTo(safeCurrent + 1), [goTo, safeCurrent]);

    if (status === "loading") {
        return (
            <div className="remote-css remote-status-page">
                <p>Connecting…</p>
            </div>
        );
    }

    if (status === "error") {
        return (
            <div className="remote-css remote-status-page">
                <p>This remote session couldn&apos;t be found. Ask the presenter for a fresh QR code.</p>
            </div>
        );
    }

    const currentSlide = slides[safeCurrent];
    const nextIndex = total > 0 ? (safeCurrent + 1) % total : 0;
    const nextSlide = slides[nextIndex];

    return (
        <div className="remote-css">
            <Helmet>
                <title>{talk?.title ? `Remote — ${talk.title}` : "Remote Control"}</title>
            </Helmet>

            {isPortrait && (
                <div className="remote-rotate-hint">
                    <span className="remote-rotate-icon" aria-hidden="true">⟳</span>
                    <p>Turn your phone sideways for a bigger preview</p>
                </div>
            )}

            <div className="remote-shell">
                <header className="remote-header">
                    <div className="remote-title">{talk?.title}</div>
                    <div className="remote-counter">{safeCurrent + 1} / {total}</div>
                </header>

                <div className="remote-previews">
                    <div className="remote-preview remote-preview-current">
                        <div className="remote-preview-label">Now</div>
                        <div className="remote-preview-frame">
                            <div className="slideshow-css slide-scaler">
                                {currentSlide && (
                                    <Slide sec={currentSlide} isActive isTitle={safeCurrent === 0} />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="remote-preview remote-preview-next">
                        <div className="remote-preview-label">Next</div>
                        <div className="remote-preview-frame remote-preview-frame-next">
                            <div className="slideshow-css slide-scaler">
                                {nextSlide && (
                                    <Slide sec={nextSlide} isActive isTitle={nextIndex === 0} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="remote-controls">
                    <button
                        className="remote-arrow remote-arrow-prev"
                        onClick={prev}
                        aria-label="Previous slide"
                    >
                        ‹
                    </button>
                    <button
                        className="remote-arrow remote-arrow-next"
                        onClick={next}
                        aria-label="Next slide"
                    >
                        ›
                    </button>
                </div>
            </div>
        </div>
    );
}
